import { useState } from "react";
import { redeemPromoCode } from "../lib/subscription";

interface PromoCodeInputProps {
  onSuccess?: () => void;
  compact?: boolean;
}

export default function PromoCodeInput({ onSuccess, compact = false }: PromoCodeInputProps) {
  const [code, setCode] = useState("");
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleRedeem = async () => {
    if (!code.trim()) return;

    setIsRedeeming(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await redeemPromoCode(code.trim());
      setSuccess(result.message || "Code redeemed successfully.");
      setCode("");
      onSuccess?.();
    } catch (err: unknown) {
      const error = err as { code?: string; message?: string };
      if (error.code === "functions/not-found") {
        setError("Invalid promo code");
      } else if (error.code === "functions/failed-precondition") {
        setError(error.message || "This code cannot be redeemed");
      } else {
        setError("Failed to redeem code. Please try again.");
      }
    } finally {
      setIsRedeeming(false);
    }
  };

  if (compact) {
    return (
      <div className="space-y-2">
        <div className="flex gap-2">
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && handleRedeem()}
            placeholder="Enter promo code"
            className="flex-1 px-3 py-2 rounded-md border border-border text-sm font-[family-name:--font-ui] focus:outline-none focus:border-accent uppercase bg-bg text-text"
            disabled={isRedeeming}
          />
          <button
            onClick={handleRedeem}
            disabled={isRedeeming || !code.trim()}
            className="px-4 py-2 bg-accent text-white rounded-md text-sm font-[family-name:--font-ui] font-medium hover:opacity-85 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
          >
            {isRedeeming ? "..." : "Apply"}
          </button>
        </div>
        {error && <p className="text-xs text-gi-high font-[family-name:--font-ui]">{error}</p>}
        {success && <p className="text-xs text-gi-low font-[family-name:--font-ui]">{success}</p>}
      </div>
    );
  }

  return (
    <div className="py-3">
      <p className="text-sm text-text font-[family-name:--font-ui] font-medium mb-3">Have a promo code?</p>

      <div className="flex gap-2">
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === "Enter" && handleRedeem()}
          placeholder="Enter your code"
          className="flex-1 px-4 py-2.5 rounded-md border border-border text-sm font-[family-name:--font-ui] focus:outline-none focus:border-accent uppercase bg-bg text-text tracking-wider"
          disabled={isRedeeming}
        />
        <button
          onClick={handleRedeem}
          disabled={isRedeeming || !code.trim()}
          className="px-5 py-2.5 bg-accent text-white rounded-md font-[family-name:--font-ui] text-sm font-medium hover:opacity-85 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-opacity"
        >
          {isRedeeming ? (
            <>
              <div className="spinner spinner--sm" style={{ width: 14, height: 14, borderWidth: 2 }} />
              Redeeming...
            </>
          ) : (
            "Redeem"
          )}
        </button>
      </div>

      {error && (
        <p className="mt-2 text-sm text-gi-high font-[family-name:--font-ui]">{error}</p>
      )}

      {success && (
        <p className="mt-2 text-sm text-gi-low font-[family-name:--font-ui]">{success}</p>
      )}
    </div>
  );
}
